import { useContext, useEffect, useRef } from "react";
import { SocketContext } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { pathsForEntity, publishNegocio, subscribeNegocio } from "./sync";

// One socket listener for the entire module, including local successful writes.
export const useNegocioBridge = () => {
  const socketManager = useContext(SocketContext);
  const { user } = useContext(AuthContext);
  const companyId = user?.companyId;
  useEffect(() => {
    if (!companyId) return undefined;
    const socket = socketManager?.getSocket?.(companyId);
    const event = `company-${companyId}-negocio`;
    const receive = (payload) => publishNegocio({ ...payload, companyId });
    const local = (event) => receive(event.detail);
    const resync = () => receive({ entity: "refresh" });
    let hiddenAt = null;
    const visibility = () => {
      if (document.visibilityState === "hidden") hiddenAt = Date.now();
      else if (hiddenAt != null) {
        if (Date.now() - hiddenAt > 15000) resync();
        hiddenAt = null;
      }
    };
    socket?.on(event, receive);
    const connectedAtMount = !!socketManager?.currentSocket?.connected;
    let firstConnect = true;
    const unsubscribeConnect = socketManager?.subscribeConnect?.(() => {
      // Initial reads already load data when the socket is connected.
      if (firstConnect && connectedAtMount) { firstConnect = false; return; }
      firstConnect = false;
      resync();
    });
    window.addEventListener("negocio:changed", local);
    window.addEventListener("online", resync);
    document.addEventListener("visibilitychange", visibility);
    return () => {
      socket?.off(event, receive);
      unsubscribeConnect?.();
      window.removeEventListener("negocio:changed", local);
      window.removeEventListener("online", resync);
      document.removeEventListener("visibilitychange", visibility);
    };
  }, [companyId, socketManager]);
};

export const useNegocioLive = (entities, onBump) => {
  const { user } = useContext(AuthContext);
  const callback = useRef(onBump);
  callback.current = onBump;
  const entityKey = (Array.isArray(entities) ? entities : [entities]).filter(Boolean).join("|");
  useEffect(() => {
    const wanted = new Set(entityKey.split("|").filter(Boolean));
    let active = true;
    let running = false;
    let pending = null;
    const run = async (change) => {
      if (running) { pending = change; return; }
      running = true;
      try {
        let next = change;
        while (active && next) {
          pending = null;
          await callback.current?.(next);
          next = pending;
        }
      } finally {
        running = false;
      }
    };
    const unsubscribe = subscribeNegocio((change) => {
      if (change.companyId != null && String(change.companyId) !== String(user?.companyId)) return;
      const paths = change.paths || new Set(pathsForEntity(change.entity));
      if (wanted.size && ![...wanted].some((entity) => paths.has(entity) || entity === change.entity)) return;
      run(change).catch(() => { /* The consumer handles request errors. */ });
    });
    return () => { active = false; pending = null; unsubscribe(); };
  }, [entityKey, user?.companyId]);
};
